import React, { useState, useEffect, useCallback } from "react";
import {
    Cloud, Plus, Pencil, Trash2, RefreshCw, AlertTriangle, Info, Check, X,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { PageLoader } from "../shared/LoadingSpinner";
import { EmptyState } from "../shared/EmptyState";
import { formatDateTime } from "../../lib/utils";
import { EntraSyncButton } from "../shared/EntraSyncButton";

export function AdminEntra() {
    const { departments, hasPermission } = useApp();
    const [mappings, setMappings] = useState([]);
    const [syncStatus, setSyncStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingMapping, setEditingMapping] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [m, s] = await Promise.all([
                api.getEntraMappings(),
                api.getEntraSyncStatus(),
            ]);
            setMappings(m);
            setSyncStatus(s);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    if (!hasPermission("admin")) return null;


    const handleDeleteMapping = async (id) => {
        if (!window.confirm("Supprimer ce mapping ?")) return;
        try {
            await api.deleteEntraMapping(id);
            toast.success("Mapping supprimé");
            loadData();
        } catch {
            toast.error("Erreur de suppression");
        }
    };

    const getDeptName = (id) => {
        const dept = departments.find((d) => d.id === id);
        return dept ? dept.name : id || "—";
    };

    if (loading) return <PageLoader />;

    const entraEnabled = syncStatus?.enabled;

    return (
        <div className="page-container" data-testid="admin-entra">
            <div className="page-header">
                <h1 className="page-title">Microsoft Entra ID</h1>
                <p className="page-subtitle">
                    Configuration SSO, mappings security groups et synchronisation
                </p>
            </div>

            {!entraEnabled && (
                <div className="flex items-center gap-3 px-4 py-3 mb-6 bg-amber-50 border border-amber-200 rounded-lg" data-testid="entra-disabled-banner">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-amber-900">Entra ID non activé</p>
                        <p className="text-xs text-amber-700">Configurez les variables ENTRA_* dans le fichier .env du backend et redémarrez le service.</p>
                    </div>
                </div>
            )}

            {/* Section 1 — Mappings */}
            <div className="card mb-6">
                <div className="card-header flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Mappings Security Groups</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Correspondance entre les groupes Entra ID et les départements du ticketing
                        </p>
                    </div>
                    <Button size="sm" onClick={() => { setEditingMapping(null); setShowForm(true); }} data-testid="add-mapping-btn">
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter
                    </Button>
                </div>
                <div className="card-body p-0">
                    {mappings.length === 0 && !showForm ? (
                        <div className="p-6">
                            <EmptyState
                                icon={Cloud}
                                title="Aucun mapping configuré"
                                description="Les utilisateurs synchronisés n'auront pas de département assigné."
                            />
                            <div className="flex items-center gap-2 mt-3 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                <p className="text-xs text-amber-800">Ajoutez au moins un mapping pour assigner automatiquement les départements.</p>
                            </div>
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="mappings-table">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    <th className="px-4 py-2.5 font-medium">Groupe Entra</th>
                                    <th className="px-4 py-2.5 font-medium">ID Groupe</th>
                                    <th className="px-4 py-2.5 font-medium">Département</th>
                                    <th className="px-4 py-2.5 font-medium text-center">Priorité</th>
                                    <th className="px-4 py-2.5 font-medium text-center">Actif</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {mappings.map((m) => (
                                    <tr key={m.id} className="hover:bg-gray-50" data-testid={`mapping-row-${m.id}`}>
                                        <td className="px-4 py-2.5 font-medium text-gray-900">{m.entra_group_name || "—"}</td>
                                        <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{m.entra_group_id ? m.entra_group_id.slice(0, 12) + "…" : "—"}</td>
                                        <td className="px-4 py-2.5">{getDeptName(m.department_id)}</td>
                                        <td className="px-4 py-2.5 text-center">{m.priority}</td>
                                        <td className="px-4 py-2.5 text-center">
                                            {m.is_active ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <X className="w-4 h-4 text-gray-300 mx-auto" />}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => { setEditingMapping(m); setShowForm(true); }} data-testid={`edit-mapping-${m.id}`}>
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteMapping(m.id)} data-testid={`delete-mapping-${m.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {showForm && (
                        <MappingForm
                            mapping={editingMapping}
                            departments={departments}
                            onSave={() => { setShowForm(false); setEditingMapping(null); loadData(); }}
                            onCancel={() => { setShowForm(false); setEditingMapping(null); }}
                        />
                    )}
                </div>
            </div>

            {/* Section 2 — Synchronisation */}
            <div className="card">
                <div className="card-header flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Synchronisation</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Synchronisation automatique toutes les {syncStatus?.sync_interval_hours || 6} heures
                        </p>
                    </div>
                    <EntraSyncButton variant="default" label="Synchroniser maintenant" onDone={loadData} />
                </div>
                <div className="card-body">
                    {syncStatus?.last_sync ? (
                        <div className="space-y-2" data-testid="last-sync-info">
                            <p className="text-sm text-gray-700">
                                Dernière synchronisation : <span className="font-medium">{formatDateTime(syncStatus.last_sync.synced_at)}</span>
                            </p>
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-green-700">{syncStatus.last_sync.created} créés</span>
                                <span className="text-blue-700">{syncStatus.last_sync.updated} mis à jour</span>
                                {syncStatus.last_sync.departed > 0 && (
                                    <span className="text-rose-700" data-testid="last-sync-departed">{syncStatus.last_sync.departed} départs</span>
                                )}
                                {syncStatus.last_sync.reactivated > 0 && (
                                    <span className="text-emerald-700">{syncStatus.last_sync.reactivated} réactivés</span>
                                )}
                                {syncStatus.last_sync.departed_skipped > 0 && (
                                    <span className="text-amber-700" title="Sécurité : trop de départs détectés d'un coup, aucun compte n'a été marqué automatiquement">{syncStatus.last_sync.departed_skipped} départs non appliqués (sécurité)</span>
                                )}
                                {syncStatus.last_sync.errors > 0 && (
                                    <span className="text-red-700">{syncStatus.last_sync.errors} erreurs</span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500" data-testid="no-sync-info">Aucune synchronisation effectuée.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

function MappingForm({ mapping, departments, onSave, onCancel }) {
    const [groupName, setGroupName] = useState(mapping?.entra_group_name || "");
    const [groupId, setGroupId] = useState(mapping?.entra_group_id || "");
    const [departmentId, setDepartmentId] = useState(mapping?.department_id || "");
    const [priority, setPriority] = useState(mapping?.priority ?? 0);
    const [isActive, setIsActive] = useState(mapping?.is_active ?? true);
    const [saving, setSaving] = useState(false);
    const [entraGroups, setEntraGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [groupsError, setGroupsError] = useState(false);

    useEffect(() => {
        api.getEntraGroups()
            .then((groups) => { setEntraGroups(groups); setLoadingGroups(false); })
            .catch(() => { setGroupsError(true); setLoadingGroups(false); });
    }, []);

    const handleGroupSelect = (selectedId) => {
        if (!selectedId) { setGroupName(""); setGroupId(""); return; }
        const group = entraGroups.find((g) => g.id === selectedId);
        if (group) {
            setGroupName(group.display_name);
            setGroupId(group.id);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!groupName.trim() || !departmentId) {
            toast.error("Le nom du groupe et le département sont obligatoires");
            return;
        }
        setSaving(true);
        try {
            const data = {
                entra_group_name: groupName.trim(),
                entra_group_id: groupId.trim(),
                department_id: departmentId,
                priority: Number(priority),
                is_active: isActive,
            };
            if (mapping) {
                await api.updateEntraMapping(mapping.id, data);
                toast.success("Mapping mis à jour");
            } else {
                await api.createEntraMapping(data);
                toast.success("Mapping créé");
            }
            onSave();
        } catch {
            toast.error("Erreur de sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const useDropdown = !groupsError && entraGroups.length > 0;

    return (
        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-gray-50" data-testid="mapping-form">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Groupe Entra *</label>
                    {loadingGroups ? (
                        <div className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-400">Chargement…</div>
                    ) : useDropdown ? (
                        <select
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={groupId}
                            onChange={(e) => handleGroupSelect(e.target.value)}
                            data-testid="mapping-group-select"
                        >
                            <option value="">Sélectionner un groupe…</option>
                            {entraGroups.map((g) => (
                                <option key={g.id} value={g.id}>{g.display_name}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="GRP-IT-Support"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            data-testid="mapping-group-name"
                        />
                    )}
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">ID Groupe</label>
                    <input
                        type="text"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                        value={groupId}
                        readOnly
                        data-testid="mapping-group-id"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Département *</label>
                    <select
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={departmentId}
                        onChange={(e) => setDepartmentId(e.target.value)}
                        data-testid="mapping-department"
                    >
                        <option value="">Choisir…</option>
                        {departments.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Priorité</label>
                    <input
                        type="number"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        data-testid="mapping-priority"
                    />
                </div>
                <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            className="rounded border-gray-300"
                            data-testid="mapping-active"
                        />
                        Actif
                    </label>
                </div>
            </div>
            {useDropdown && (
                <p className="text-xs text-gray-400 mb-3">Groupes chargés depuis Microsoft Entra ID. L'ID est rempli automatiquement.</p>
            )}
            {groupsError && (
                <p className="text-xs text-amber-600 mb-3">Impossible de charger les groupes Entra. Saisissez le nom manuellement.</p>
            )}
            <div className="flex items-center gap-2">
                <Button type="submit" size="sm" disabled={saving} data-testid="mapping-save-btn">
                    {saving ? "Enregistrement…" : (mapping ? "Mettre à jour" : "Créer le mapping")}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid="mapping-cancel-btn">
                    Annuler
                </Button>
            </div>
        </form>
    );
}
